import { inject, InjectionToken } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import L, {
  divIcon,
  icon,
  LatLng,
  latLngBounds,
  marker,
  tileLayer,
  WMSOptions,
} from 'leaflet';
import { Proj4GeoJSONFeature } from 'proj4leaflet';
import { wmtsTileSize } from '../projections';
import NonTiledLayer from './nontiledlayer';

export const LAYER_QUERY_PARAM_KEY = 'l';
export const BASE_LAYER_QUERY_PARAM_KEY = 'b';
export const PROFILE_QUERY_PARAM_KEY = 'e';

export const metro = latLngBounds(
  [-38.2124301, 144.5230242],
  [-37.3901094, 145.5464025],
);
export const victoria = latLngBounds(
  [-39.30260682853045, 140.6737429990074],
  [-33.87013625423628, 150.39414910555496],
);

export const FALLBACK_ZOOM_LEVEL = 13;

const geoserverUrl =
  location.protocol +
  '//maps.' +
  location.hostname.replace('embedded.', '') +
  ':' +
  location.port +
  '/geoserver';

export const wmsUrl = geoserverUrl + '/wms';
export const wfsUrl = geoserverUrl + '/wfs';

const vicmapUrl = 'https://base.maps.vic.gov.au/wms';

const wmsTileSize = 768;

export const mapAttribution =
  '© State Government of Victoria, Department of Transport and Planning (Vicmap®)';

export function basemapFactory(type: 'Blank' | 'Imagery' | 'Map') {
  switch (type) {
    case 'Imagery':
      return tileLayer.wms(vicmapUrl, {
        layers: 'AERIAL_VG2020',
        tileSize: wmtsTileSize,
      });
    case 'Map':
      return tileLayer.wms(vicmapUrl, {
        layers: 'CARTO_VG2020',
        tileSize: wmtsTileSize,
      });

    default:
      return null;
  }
}

export const BASE_LAYERS = new InjectionToken('BASE_LAYERS', {
  factory: () => ({
    Blank: null,
    Imagery: basemapFactory('Imagery'),
    Map: basemapFactory('Map'),
  }),
});

export const BASE_LAYER_LIST = new InjectionToken('BASE_LAYER_LIST', {
  factory: () => Object.keys(inject(BASE_LAYERS)),
});

export const DEFAULT_BASE_LAYER = 'Map';

export function createOverlayLayer(
  layer: string,
  useTiled: boolean,
  isIos: boolean,
  zIndex: number,
  styles?: string,
) {
  const options: WMSOptions = {
    layers: layer,
    format: 'image/png',
    transparent: true,
    id: layer,
    zIndex,
    pane: 'overlayPane',
    crossOrigin: true,
  };

  if (styles) {
    options.styles = styles;
  }

  if (!useTiled || isIos) {
    // @ts-expect-error TS2339: Property 'WMS' does not exist on type '(new (...args: any[]) => any) & typeof Class'.
    return new NonTiledLayer.WMS(wmsUrl, options);
  } else {
    options.tileSize = wmsTileSize;
    return tileLayer.wms(wmsUrl, options);
  }
}

export enum MapEnquiryOption {
  PropertyEnquiry = 'LASSI',
  VicnamesEnquiry = 'VICNAMES',
  SurveyMarkEnquiry = 'SMES',
}

export const DEFAULT_MAP_ZOOM_LEVEL = 13;
export const CURRENT_LOCATION_QUERY_PARAM = 'current';

/* Measurement related */
export enum MeasureType {
  DISTANCE = 'Measure distance',
  AREA = 'Measure area',
}

export interface IMeasureVertex {
  id: string;
  latlng: LatLng;
}

export interface IMeasureSegment {
  label: string;
  from: LatLng;
  to: LatLng;
}

export function createSearchResultGeometry(geoJson: Proj4GeoJSONFeature) {
  return L.Proj.geoJson(geoJson, {
    style: {
      className: 'pointer-events-none',
    },
    pointToLayer: (_: never, latlng: LatLng) => createMarker(latlng),
  });
}

export function createMarker(latlng: LatLng) {
  return marker(latlng, {
    icon: icon({
      iconAnchor: [25 / 2, 41],
      iconUrl: '/media/marker-icon.png',
      shadowUrl: '/media/marker-shadow.png',
    }),
    interactive: false,
  });
}

export function createDuplicateSearchMarker(latlng: LatLng) {
  return marker(latlng, {
    icon: divIcon({
      html: `
        <span class="duplicate-marker-crosshair">
          <span class="duplicate-marker-crosshair__vertical"></span>
          <span class="duplicate-marker-crosshair__horizontal"></span>
        </span>
      `,
      className: 'duplicate-marker-icon',
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    }),
    interactive: false,
  });
}
